/* eslint-disable complexity */
/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { LegView } from '@sequence/models/view/leg.view';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { AlertService } from '@shared/services/alert/alert.service';
import { DecsService } from '@shared/services/decs/decs.service';
import { map, Observable, of } from 'rxjs';
import {
  InboundConnection,
  InboundConnectionsView,
} from './models/inbound-connections.view';
import { DateTime, Duration } from 'luxon';

@Injectable({
  providedIn: 'root',
})
export class InboundConnectionsService {
  constructor(
    private decsService: DecsService,
    private alertService: AlertService,
  ) {}

  getInboundConnections(
    leg: LegView,
    isRefresh = false,
  ): Observable<RequestState<InboundConnectionsView>> {
    // Only available within 3 days.
    if (!this.isAvailable(leg)) {
      return of(
        new RequestState<InboundConnectionsView>(
          RequestStatus.IsCompleteWithResponse,
          {
            connections: [],
            notAvailable: true,
            timestamp: DateTime.now(),
          },
        ),
      );
    }

    const cmd = `G*L${leg.flightNumber}/${
      leg.depTimeSched?.toFormat('ddMMM') || ''
    }${leg.depStation}/BX`;

    return this.decsService.submitResRequest({ cmd }, isRefresh).pipe(
      map((response) => {
        if (response.hasError) {
          this.showErrorAlert();
        }

        if (!response.response) {
          return response.into<InboundConnectionsView>();
        }

        const sabreText = response.response.result;
        const connectionsView = this.parseConnections(
          sabreText,
          response.response.timestamp,
        );

        for (const connection of connectionsView.connections) {
          connection.delta = this.getDelta(connection, leg);
        }

        return response.into<InboundConnectionsView>(connectionsView);
      }),
    );
  }

  // Check if scheduled departure is within 3 calendar days.
  private isAvailable(leg: LegView): boolean {
    if (!leg.depTimeSchedUTC) {
      return false;
    }

    const diff = leg.depTimeSchedUTC.diff(DateTime.now().toUTC()).as('days');

    if (diff < 3) {
      return true;
    }

    return false;
  }

  private parseConnections(
    text: string,
    timestamp: string,
  ): InboundConnectionsView {
    // Examples:
    // No inbound connection
    //  5  LEE        EVELY  BO2 R   CLT  2A*F  2
    // Already arrived
    //  1  MOSS       WENDI      J   CLT  1A*F  1 AA 1911  IN1559 C21
    // Other airline inbound connection
    //  71  HABESHAMOG FIKIR  BT3 V   DEN 17A*F NB QR 731  SKD0950
    // Inbound connection not arrived (two formats)
    //  95  HAGEMEIER  ELIZA      Y   DEN 21A*F  1 AA 1396/ETA1149 C39
    // 100  LARA       NICOL      L   DEN 22A*F  1 AA 0413 ETA1210 C34

    // Parse the seat, airline, flight number, status, time, and gate.
    const pattern =
      /[A-Z]{3}\s+(?<seat>\d{1,2}[A-Z]{1})[*|\s][A-Z]{1}.*(?<airline>[A-Z]{2})\s+(?<flight>\d{1,4}).*((?<status>IN)|(?<status>ETA)|(?<status>SKD))(?<time>\d{4})(?<gate>.*)/;

    const connections: InboundConnectionsView = {
      connections: [],
      timestamp: DateTime.fromISO(timestamp, { zone: 'UTC' }),
      notAvailable: false,
    };

    const lines = text.split('\n');

    for (const line of lines) {
      const match = line.match(pattern);
      if (match) {
        // Remove any leading zeroes from flight number.
        const flight = parseInt(match.groups?.['flight'] || '0').toString();

        // Insert colon into time.
        let time = match.groups?.['time'];
        let timeHours = 0;
        let timeMinutes = 0;

        if (time && time.length === 4) {
          timeHours = parseInt(time.substring(0, 2));
          timeMinutes = parseInt(time.substring(2));
          time = `${time.substring(0, 2)}:${time.substring(2)}`;
        } else {
          time = '';
        }

        const connection: InboundConnection = {
          seat: match.groups?.['seat'] || '',
          airline: match.groups?.['airline'] || '',
          flight,
          status: match.groups?.['status'] || '',
          time,
          timeHours,
          timeMinutes,
          gate: match.groups?.['gate']?.trim() || '',
        };
        connections.connections.push(connection);
      }
    }

    return connections;
  }

  // Calculate time between connection arrival and flight departure.
  private getDelta(connection: InboundConnection, leg: LegView): Duration {
    // The date isn't specified, so assume that the smallest value is correct.
    if (leg) {
      const depTime =
        leg.depTimeActual || leg.depTimeResched || leg.depTimeSched;

      if (depTime) {
        let arrDateTime = DateTime.fromObject(
          {
            year: depTime.year,
            month: depTime.month,
            day: depTime.day,
            hour: connection.timeHours,
            minute: connection.timeMinutes,
          },
          { zone: 'utc' },
        );

        let delta = depTime.diff(arrDateTime);

        // If the delta is negative and greater than 12 hours,
        // assume that the connection date is the previous day.
        if (delta.as('minutes') < -720) {
          arrDateTime = arrDateTime.minus({ days: 1 });
          delta = depTime.diff(arrDateTime);
        }

        return delta;
      }
    }
    return Duration.fromObject({ minutes: 0 });
  }

  private showErrorAlert(): void {
    this.alertService.setAlert({
      type: 'error',
      message: 'Unable to get passenger connection information.',
    });
  }
}
